import { supabase } from '../lib/supabase';

// Fetch user profile with all related data
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { data: null, error };
  }
};

// Fetch user quiz results
export const getUserQuizResults = async (userId) => {
  try {
    const { data, error } = await supabase?.from('quiz_results')?.select('*')?.eq('user_id', userId)?.order('completed_at', { ascending: false });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching quiz results:', error);
    return { data: [], error };
  }
};

// Fetch user achievements
export const getUserAchievements = async (userId) => {
  try {
    const { data, error } = await supabase?.from('achievements')?.select('*')?.eq('user_id', userId)?.order('earned_at', { ascending: false });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching achievements:', error);
    return { data: [], error };
  }
};

// Fetch user progress reports
export const getUserProgressReports = async (userId) => {
  try {
    const { data, error } = await supabase?.from('progress_reports')?.select('*')?.eq('user_id', userId)?.order('report_date', { ascending: false });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching progress reports:', error);
    return { data: [], error };
  }
};

// Fetch parent's children
export const getParentChildren = async (parentId) => {
  try {
    const { data, error } = await supabase?.from('parent_children')?.select(`
        id,
        relationship,
        created_at,
        child:user_profiles!parent_children_child_id_fkey(
          id,
          full_name,
          email,
          age,
          grade,
          school,
          created_at
        )
      `)?.eq('parent_id', parentId);

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching parent children:', error);
    return { data: [], error };
  }
};

// Fetch child's quiz results, achievements, and progress
export const getChildProgress = async (childId) => {
  try {
    const [quizResults, achievements, progressReports] = await Promise.all([
      getUserQuizResults(childId),
      getUserAchievements(childId),
      getUserProgressReports(childId)
    ]);

    return {
      quizResults: quizResults?.data,
      achievements: achievements?.data,
      progressReports: progressReports?.data
    };
  } catch (error) {
    console.error('Error fetching child progress:', error);
    return {
      quizResults: [],
      achievements: [],
      progressReports: []
    };
  }
};

// Fetch counselor's students
export const getCounselorStudents = async (counselorId) => {
  try {
    const { data, error } = await supabase?.from('counselor_students')?.select(`
        id,
        priority_level,
        notes,
        created_at,
        student:user_profiles!counselor_students_student_id_fkey(
          id,
          full_name,
          email,
          grade,
          school,
          created_at
        )
      `)?.eq('counselor_id', counselorId);

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching counselor students:', error);
    return { data: [], error };
  }
};

// Fetch student's complete data for counselor view
export const getStudentCompleteData = async (studentId) => {
  try {
    const [profile, quizResults, achievements, progressReports] = await Promise.all([
      getUserProfile(studentId),
      getUserQuizResults(studentId),
      getUserAchievements(studentId),
      getUserProgressReports(studentId)
    ]);

    return {
      profile: profile?.data,
      quizResults: quizResults?.data,
      achievements: achievements?.data,
      progressReports: progressReports?.data
    };
  } catch (error) {
    console.error('Error fetching student complete data:', error);
    return {
      profile: null,
      quizResults: [],
      achievements: [],
      progressReports: []
    };
  }
};

// Calculate engagement score based on activity
export const calculateEngagementScore = (quizResults, achievements) => {
  const quizScore = Math.min((quizResults?.length || 0) * 5, 50);
  const achievementScore = Math.min((achievements?.length || 0) * 8, 50);
  return Math.min(quizScore + achievementScore, 100);
};

// Calculate progress percentage
export const calculateProgressPercentage = (quizResults, achievements) => {
  const totalQuizzes = 15;
  const totalAchievements = 12;
  
  const quizProgress = ((quizResults?.length || 0) / totalQuizzes) * 60;
  const achievementProgress = ((achievements?.length || 0) / totalAchievements) * 40;
  
  return Math.min(Math.round(quizProgress + achievementProgress), 100);
};